'use client';

import { useEffect, useMemo } from 'react';

import { classNames } from '@bytel/product-wall/helpers';
import {
    Align,
    Box,
    BoxContent,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Container,
    Icon,
    IconName,
    IconSize,
    Justify,
    Price,
    PriceLevel,
    Row,
    Rows,
    Text,
    TextLevels,
    TrilogyColor,
    TypographyAlign,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { FaiType, MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';

import { InlinePrice } from '@components/inline-price';

import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

import { StickyItem } from './sticky-item';

interface GroupedPlan {
    gencode: string;
    name: string;
    count: number;
    price: number;
}

type StickyMenuProps = {
    ctaLabel?: string;
    price?: number;
    fai: Product | FaiType;
    plans: (Product | MobilePlanType)[];
    onTicketValidate?: ClickEvent;
    isVisible: boolean;
    className?: string;
};

export function StickyMenu({
    ctaLabel,
    price,
    fai,
    plans,
    onTicketValidate,
    isVisible,
    className = '',
}: Readonly<StickyMenuProps>) {
    const groupedPlans = useMemo(() => {
        return plans.reduce(
            (grouped, plan) => {
                if (!grouped[plan.gencode]) {
                    grouped[plan.gencode] = {
                        gencode: plan.gencode,
                        name: plan.name,
                        count: 1,
                        price: plan.details.price.final,
                    };
                } else {
                    grouped[plan.gencode]!.count += 1;
                }
                return grouped;
            },
            {} as Record<string, GroupedPlan>,
        );
    }, [plans]);

    useEffect(() => {
        // Position omnikit widget before this sticky container
        window.omnikit = { insertBeforeId: 'promo-simulator-sticky-menu' };
    }, []);

    return (
        <View
            className={classNames('sticky-bar', className, 'sticky-bottom', isVisible ? 'visible' : '')}
            id='promo-simulator-sticky-menu'
        >
            <Container>
                <Box>
                    <BoxContent>
                        <Columns multiline className='is-spaced-around gap-small' verticalAlign={Align.CENTER}>
                            <Column>
                                <Columns verticalAlign={Align.CENTER} className='is-flex-wrap'>
                                    <Column narrow>
                                        <View className='is-flex-mobile' justify={Justify.SPACE_BETWEEN}>
                                            <Text
                                                marginless
                                                typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                                level={TextLevels.ONE}
                                            >
                                                Votre sélection
                                            </Text>
                                            <div>
                                                <View className='is-hidden-tablet'>
                                                    <InlinePrice amount={price} period='mois' />
                                                </View>
                                                <View className='is-hidden-mobile'>
                                                    <Price
                                                        level={PriceLevel.FOUR}
                                                        align={Align.END}
                                                        amount={price}
                                                        period='mois'
                                                    />
                                                </View>
                                            </div>
                                        </View>
                                    </Column>
                                    <Column>
                                        <Rows gap={1} className='is-flex'>
                                            <Row>
                                                <StickyItem
                                                    iconName={IconName.BOX_WIFI_6}
                                                    offerType='Offre fibre'
                                                    period='mois'
                                                    offerLabel={fai.name}
                                                    price={fai.details.price.final}
                                                />
                                            </Row>

                                            <Row>
                                                <Columns
                                                    gap={1}
                                                    mobile
                                                    className={classNames(
                                                        'is-spaced-between-mobile is-aligned-center-tablet',
                                                        className ?? '',
                                                    )}
                                                >
                                                    <Column className='is-hidden-mobile' narrow>
                                                        <Icon
                                                            name={
                                                                plans.length > 1
                                                                    ? IconName.SIM_CARD_BIG
                                                                    : IconName.SIM_CARD
                                                            }
                                                            size={IconSize.SMALL}
                                                            circled
                                                            backgroundColor={TrilogyColor.INFO_FADE}
                                                            color={TrilogyColor.MAIN}
                                                        />
                                                    </Column>
                                                    <Column narrow className='is-hidden-tablet'>
                                                        <Text
                                                            level={TextLevels.TWO}
                                                            marginless
                                                            typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                                        >
                                                            {`Forfait${plans.length > 1 ? 's' : ''} mobile`}:
                                                        </Text>
                                                    </Column>
                                                    <Column narrow className='is-flex is-flex-col is-hidden-tablet'>
                                                        {Object.values(groupedPlans).map((group) => (
                                                            <Text
                                                                level={TextLevels.TWO}
                                                                typo={TypographyAlign.TEXT_RIGHT}
                                                                marginless
                                                                key={group.name}
                                                            >
                                                                {group.count}&nbsp;
                                                                {group.name.replace(' ', '\u00A0')} (
                                                                <InlinePrice
                                                                    level={TextLevels.TWO}
                                                                    amount={group.price}
                                                                    period='mois'
                                                                    typo={[TypographyBold.TEXT_WEIGHT_NORMAL]}
                                                                />
                                                                )
                                                            </Text>
                                                        ))}
                                                    </Column>
                                                    <Column className='is-hidden-mobile is-flex is-flex-wrap gap-small'>
                                                        <Text
                                                            level={TextLevels.TWO}
                                                            marginless
                                                            typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                                        >
                                                            {`Forfait${plans.length > 1 ? 's' : ''} mobile`}&nbsp;:
                                                        </Text>
                                                        {Object.values(groupedPlans).map((group, index) => (
                                                            <Text
                                                                key={group.name}
                                                                level={TextLevels.TWO}
                                                                className='text-nowrap'
                                                                marginless
                                                            >
                                                                {group.count} {group.name} (
                                                                <InlinePrice
                                                                    level={TextLevels.TWO}
                                                                    amount={group.price}
                                                                    period='mois'
                                                                    typo={[TypographyBold.TEXT_WEIGHT_NORMAL]}
                                                                />
                                                                )
                                                                {index < Object.values(groupedPlans).length - 1 &&
                                                                    ' + '}
                                                            </Text>
                                                        ))}
                                                    </Column>
                                                </Columns>
                                            </Row>
                                        </Rows>
                                    </Column>
                                </Columns>
                            </Column>
                            <Column narrow>
                                <Button onClick={onTicketValidate} variant={ButtonVariant.CONVERSION}>
                                    {ctaLabel}
                                </Button>
                            </Column>
                        </Columns>
                    </BoxContent>
                </Box>
            </Container>
        </View>
    );
}
